type UploadedRowsPayload = {
  rowsStorageKey: string;
  rowsFormat: "json" | "json.gz";
  rowCount: number;
};

type UploadParams = {
  generateUploadUrl: () => Promise<{ key: string; url: string }>;
  headers: string[];
  rows: string[][];
  /** The source page of each row, kept when the rows record them. */
  rowPageNumbers?: number[];
};

async function buildRowsBlob(
  payload: Pick<UploadParams, "headers" | "rows" | "rowPageNumbers">
): Promise<{ blob: Blob; rowsFormat: "json" | "json.gz"; contentType: string }> {
  const json = JSON.stringify(payload);
  const plainBlob = new Blob([json], { type: "application/json" });

  if (typeof CompressionStream === "undefined") {
    return {
      blob: plainBlob,
      rowsFormat: "json",
      contentType: "application/json",
    };
  }

  try {
    const compressedStream = plainBlob
      .stream()
      .pipeThrough(new CompressionStream("gzip"));
    const compressedBlob = await new Response(compressedStream).blob();
    return {
      blob: compressedBlob,
      rowsFormat: "json.gz",
      contentType: "application/json+gzip",
    };
  } catch {
    return {
      blob: plainBlob,
      rowsFormat: "json",
      contentType: "application/json",
    };
  }
}

export async function uploadTableRowsPayload({
  generateUploadUrl,
  headers,
  rows,
  rowPageNumbers,
}: UploadParams): Promise<UploadedRowsPayload> {
  const { key, url } = await generateUploadUrl();
  const { blob, rowsFormat, contentType } = await buildRowsBlob({ headers, rows, rowPageNumbers });

  const response = await fetch(url, {
    method: "PUT",
    body: blob,
    headers: { "Content-Type": contentType },
  });
  if (!response.ok) {
    throw new Error(`Row payload upload failed: ${response.status}`);
  }

  return {
    rowsStorageKey: key,
    rowsFormat,
    rowCount: rows.length,
  };
}

/**
 * Row pages after an edit, from the pages before it and each saved row's
 * origin (`rowOrigins`, null for an added row). An added row takes the page of
 * the row above it; undefined when no row has a page to give.
 */
export function rowPagesAfterEdit(
  previousRowPageNumbers: number[],
  rowOrigins: Array<number | null>
): number[] | undefined {
  let page = previousRowPageNumbers.at(-1);
  const pages: number[] = [];
  for (const origin of rowOrigins) {
    if (origin !== null) page = previousRowPageNumbers[origin];
    if (page === undefined) return undefined;
    pages.push(page);
  }
  return pages;
}
